import { TakeRecord } from '../types/interview';

interface PickYourAnswerProps {
  takes: TakeRecord[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  isMobile?: boolean;
}

function formatDuration(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const m = Math.floor(safe / 60).toString().padStart(2, '0');
  const s = (safe % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

const PickYourAnswer = ({ takes, selectedIndex, onSelect, isMobile = false }: PickYourAnswerProps) => {
  // VI-702: reflect the SELECTED take, not just the count recorded (e.g. "2 OF 3"
  // while viewing take 2). Denominator is the number of takes recorded so it never
  // implies a take that doesn't exist.
  const currentPosition = selectedIndex >= 0 ? selectedIndex + 1 : takes.length;

  return (
    <div style={{ marginTop: '1.5rem' }}>
      {/* "PICK YOUR ANSWER" label */}
      <p style={{
        margin: '0 0 6px 0',
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.09em',
        textTransform: 'uppercase',
        color: '#6c757d',
      }}>
        Pick Your Answer
      </p>

      {/* Selected take position, in accent blue */}
      <p style={{
        margin: '0 0 0.75rem 0',
        fontSize: '0.85rem',
        fontWeight: 700,
        color: '#0060AA',
      }}>
        {currentPosition} OF {takes.length}
      </p>

      {/* Thumbnail strip — compact row on both desktop and mobile. VI-702: on
          mobile the takes are a 3-across row (max 3 takes fit one row) so they
          read as pickable thumbnails rather than full-size previews. */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: '0.5rem',
        flexWrap: 'wrap',
      }}>
        {takes.map((take, i) => {
          const isSelected = i === selectedIndex;
          return (
            <button
              key={i}
              onClick={() => onSelect(i)}
              style={{
                position: 'relative',
                // VI-702: 3-across on mobile (two 0.5rem gaps = 1rem), fixed 108px on desktop
                width: isMobile ? 'calc((100% - 1rem) / 3)' : '108px',
                aspectRatio: '16/9',
                padding: 0,
                border: isSelected ? '2.5px solid #0d6efd' : '2px solid #aac0d6',
                borderRadius: '5px',
                overflow: 'hidden',
                cursor: 'pointer',
                background: '#1a1a2e',
                flexShrink: 0,
                transition: 'border-color 0.15s ease',
              }}
            >
              {/* Thumbnail image — always full colour; overlay dims unselected */}
              {take.thumbnail ? (
                <img
                  src={take.thumbnail}
                  alt={`Take ${i + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', background: '#1a1a2e' }} />
              )}

              {/* White semi-transparent overlay for unselected takes */}
              {!isSelected && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.45)',
                  pointerEvents: 'none',
                }} />
              )}

              {/* Duration badge — VI-702: compact centered chip. Mobile thumbs are now
                  the same small 3-across size as the desktop strip, so one sizing fits both. */}
              <span style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                minWidth: '3rem',
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.55)',
                color: '#323235',
                fontSize: '0.8rem',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                padding: '4px 8px',
                borderRadius: '4px',
                letterSpacing: '0.02em',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}>
                {formatDuration(take.duration)}
              </span>

              {/* Blue filled checkmark — top-right corner, selected only */}
              {isSelected && (
                <span style={{
                  position: 'absolute',
                  top: '3px',
                  right: '3px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: '#0d6efd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}>
                  <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                    <path
                      d="M1 3.2L3.4 6L8 1"
                      stroke="white"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PickYourAnswer;
